import { useState } from "react"
import PropTypes from "prop-types"
import { PhotoProvider, PhotoView } from "react-photo-view"
import "react-photo-view/dist/react-photo-view.css"
import CircularProgress from "@mui/material/CircularProgress"
import Tooltip from "@mui/material/Tooltip"
import ClickAwayListener from "@mui/material/ClickAwayListener"
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined"
import { useTheme } from "@mui/material/styles"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDBadge from "components/MDBadge"
import MDButton from "components/MDButton"
import MDAvatar from "components/MDAvatar"
import { useMaterialUIController } from "context"
import { buildImageUrl, formatIsoDate } from "layouts/ingresos-raw/data/ingresosTableData"
import config from "config"

function DuplicateItem({ item }) {
  const [open, setOpen] = useState(false)
  const [controller] = useMaterialUIController()
  const { darkMode } = controller
  const theme = useTheme()
  const handleToggle = () => setOpen((prev) => !prev)
  const handleClose = () => setOpen(false)

  const imageUrl = buildImageUrl(config, item.imagePath)

  return (
    <MDBox
      display="flex"
      alignItems="center"
      gap={1.5}
      p={1}
      sx={({ palette: { light }, borders: { borderWidth, borderRadius } }) => ({
        border: `${borderWidth[1]} solid ${light.main}`,
        borderRadius: borderRadius.md,
      })}
    >
      <PhotoView src={imageUrl}>
        <MDAvatar src={imageUrl} name={item.nombre} size="lg" style={{ cursor: "pointer" }} />
      </PhotoView>
      <MDBox lineHeight={1}>
        <MDBox display="flex" alignItems="center" gap={0.5}>
          <MDTypography variant="caption" fontWeight="regular" display="block">
            {item.nombre ?? "NO IDENTIFICADO"}
          </MDTypography>
          <ClickAwayListener onClickAway={handleClose}>
            <div>
              <Tooltip
                title={
                  <MDTypography variant="caption" color="white" display="block">
                    <b>ID Traza:</b> {item.serialNo}
                  </MDTypography>
                }
                open={open}
                onClose={handleClose}
                disableFocusListener
                disableHoverListener
                disableTouchListener
              >
                <InfoOutlineIcon
                  fontSize="small"
                  style={{
                    cursor: "pointer",
                    color: darkMode ? theme.palette.warning.light : theme.palette.warning.main,
                  }}
                  onClick={handleToggle}
                />
              </Tooltip>
            </div>
          </ClickAwayListener>
        </MDBox>
        {(item.documentType || item.documentNumber) && (
          <MDTypography variant="caption" color="text" display="block">
            {item.documentType ? `${item.documentType}: ` : ""}
            {item.documentNumber ?? ""}
          </MDTypography>
        )}
        <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={2} mt={0.5}>
          <MDBox display="flex" alignItems="center" gap={0.5} ml={-1}>
            <MDTypography variant="caption" color="text">
              Acceso:
            </MDTypography>
            <MDBadge
              badgeContent={
                item.inOut === null || item.inOut === "" ? "N/A" : item.inOut === "in" ? "Entrada" : "Salida"
              }
              color={
                item.inOut === null || item.inOut === ""
                  ? "dark"
                  : item.inOut === "in"
                    ? "success"
                    : "error"
              }
              variant="gradient"
              size="sm"
            />
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={0.5} ml={-1}>
            <MDTypography variant="caption" color="text">
              ¿Es Cliente?:
            </MDTypography>
            <MDBadge
              badgeContent={item.esCliente === null ? "No" : item.esCliente ? "Si" : "No"}
              color={item.esCliente === null ? "error" : item.esCliente ? "success" : "error"}
              variant="gradient"
              size="sm"
            />
          </MDBox>
        </MDBox>
        <MDTypography variant="caption" color="text" fontWeight="bold" display="block" mt={0.5}>
          {formatIsoDate(item.dateTime, "DATE")} {formatIsoDate(item.dateTime, "TIME")} hs.
        </MDTypography>
      </MDBox>
    </MDBox>
  )
}

DuplicateItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.number,
    serialNo: PropTypes.string,
    inOut: PropTypes.string,
    documentType: PropTypes.string,
    documentNumber: PropTypes.string,
    nombre: PropTypes.string,
    esCliente: PropTypes.bool,
    imagePath: PropTypes.string,
    dateTime: PropTypes.string,
  }).isRequired,
}

function DuplicatesPanel({ loading, error, items, onRetry }) {
  if (loading) {
    return (
      <MDBox display="flex" alignItems="center" gap={1} py={1}>
        <CircularProgress size={18} />
        <MDTypography variant="caption" color="text">
          Cargando detecciones repetidas...
        </MDTypography>
      </MDBox>
    )
  }

  if (error) {
    return (
      <MDBox display="flex" alignItems="center" gap={2} py={1}>
        <MDTypography variant="caption" color="error">
          {error}
        </MDTypography>
        <MDButton size="small" variant="outlined" color="error" onClick={onRetry}>
          Reintentar
        </MDButton>
      </MDBox>
    )
  }

  if (!items || items.length === 0) {
    return (
      <MDTypography variant="caption" color="text" fontStyle="italic">
        Sin datos adicionales
      </MDTypography>
    )
  }

  return (
    <MDBox>
      <MDTypography variant="caption" color="text" fontWeight="bold" display="block" mb={1}>
        Detecciones repetidas ({items.length})
      </MDTypography>
      <MDBox display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
        <PhotoProvider>
          {items.map((item) => (
            <DuplicateItem key={item.id} item={item} />
          ))}
        </PhotoProvider>
      </MDBox>
    </MDBox>
  )
}

DuplicatesPanel.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      serialNo: PropTypes.string,
      dedupSerialOriginal: PropTypes.string,
      ipDevice: PropTypes.string,
      inOut: PropTypes.string,
      documentType: PropTypes.string,
      documentNumber: PropTypes.string,
      nombre: PropTypes.string,
      esCliente: PropTypes.bool,
      imagePath: PropTypes.string,
      dateTime: PropTypes.string,
    })
  ),
  onRetry: PropTypes.func,
}

DuplicatesPanel.defaultProps = {
  loading: false,
  error: null,
  items: [],
  onRetry: () => {},
}

export default DuplicatesPanel
